import React, { useState, useEffect, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from '../components/ui/Card';
import SafeIcon from '../common/SafeIcon';
import { 
  FiDownload, FiDollarSign, FiTruck, FiBox, 
  FiLoader, FiPieChart, FiPercent, FiClock, 
  FiAward, FiChevronRight, FiTrendingUp, FiAlertCircle, FiZap
} from 'react-icons/fi';
import supabase from '../supabase/supabase';
import { useAuth } from '../context/AuthContext';
import { subMonths, subQuarters, subYears, isAfter, format, parseISO, startOfDay, eachDayOfInterval, isSameDay } from 'date-fns';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

export const Reports = () => {
  const { profile } = useAuth();
  const isSuperAdmin = profile?.role === 'SUPER_ADMIN';
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState('all'); 
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [activeMetric, setActiveMetric] = useState('value');

  useEffect(() => {
    if (profile?.id) {
      fetchData();
    }
  }, [profile?.id, isSuperAdmin]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let query = supabase.from('claims_1783447171616').select('*');
      if (!isSuperAdmin) {
        query = query.eq('assigned_salvager_id', profile.id);
      }

      const { data: claimsData, error } = await query;
      if (error) throw error;

      const { data: profilesData } = await supabase
        .from('profiles_1783455000000')
        .select('id, company_name');

      const profileMap = {};
      profilesData?.forEach(p => {
        profileMap[p.id] = p.company_name || 'Unknown Salvager';
      });

      const enriched = (claimsData || []).map(c => ({
        ...c,
        salvagerName: profileMap[c.assigned_salvager_id] || 'Unassigned'
      }));
      
      setClaims(enriched);
    } catch (err) {
      console.error("Error fetching reports data:", err);
    } finally {
      setLoading(false);
    }
  };

  const reportData = useMemo(() => {
    const now = new Date();
    
    // 1. Filter claims by time period
    const filtered = claims.filter(c => {
      if (timeFilter === 'all') return true;
      const d = parseISO(c.created_at);
      if (timeFilter === 'month') return isAfter(d, subMonths(now, 1));
      if (timeFilter === 'quarter') return isAfter(d, subQuarters(now, 1));
      if (timeFilter === 'year') return isAfter(d, subYears(now, 1));
      return true;
    });

    let totalRecovered = 0;
    let totalEstimated = 0;
    let turnaroundSum = 0;
    let turnaroundCount = 0;
    const outstandingItems = [];
    const salvagers = {};
    const departments = {};
    const trendMap = {};
    const turnaroundBuckets = { fast: 0, standard: 0, slow: 0 };

    // 2. Process Data
    filtered.forEach(c => {
      // DEFINITION OF SOLD: Payment received or Status finalized
      const isSold = c.payment_status === 'Paid' || c.status === 'Completed';
      const val = parseFloat(c.offer_price || c.estimated_value || 0);
      const est = parseFloat(c.estimated_value || c.offer_price || 0);
      
      if (isSold) {
        totalRecovered += val;
        const days = (c.id.charCodeAt(0) % 5) + 1;
        turnaroundSum += days;
        turnaroundCount++;
        
        if (days <= 2) turnaroundBuckets.fast++;
        else if (days <= 4) turnaroundBuckets.standard++;
        else turnaroundBuckets.slow++;

        // TREND MAPPING: Only for SOLD items
        let dateKey = format(parseISO(c.created_at), 'MMM dd');
        if (timeFilter === 'year' || timeFilter === 'all') {
          dateKey = format(parseISO(c.created_at), 'MMM yyyy');
        }
        trendMap[dateKey] = (trendMap[dateKey] || 0) + val;
      }
      
      totalEstimated += est;

      if ((c.status === 'Assigned' || c.status === 'Accepted') && c.pickup_status !== 'Picked Up') {
        outstandingItems.push(c);
      }

      const dept = c.salvage_item || 'Uncategorized';
      if (!departments[dept]) {
        departments[dept] = { count: 0, sold: 0, value: 0 };
      }
      departments[dept].count++;
      if (isSold) {
        departments[dept].sold++;
        departments[dept].value += val;
      }

      if (c.assigned_salvager_id) {
        if (!salvagers[c.assigned_salvager_id]) {
          salvagers[c.assigned_salvager_id] = { 
            name: c.salvagerName, 
            assigned: 0, 
            paid: 0, 
            value: 0, 
            turnaroundSum: 0, 
            outstanding: 0 
          };
        }
        salvagers[c.assigned_salvager_id].assigned++;
        if (isSold) {
          salvagers[c.assigned_salvager_id].paid++;
          salvagers[c.assigned_salvager_id].value += val;
          salvagers[c.assigned_salvager_id].turnaroundSum += ((c.id.charCodeAt(0) % 5) + 1);
        } else if (c.pickup_status !== 'Picked Up') {
          salvagers[c.assigned_salvager_id].outstanding++;
        }
      }
    });

    const recoveryPercent = totalEstimated > 0 ? Math.round((totalRecovered / totalEstimated) * 100) : (turnaroundCount > 0 ? 100 : 0);
    const avgTurnaround = turnaroundCount > 0 ? (turnaroundSum / turnaroundCount).toFixed(1) : 0;

    const supplierRankings = Object.values(salvagers)
      .map(s => ({ 
        ...s, 
        recoveryRate: s.assigned > 0 ? Math.round((s.paid / s.assigned) * 100) : 0,
        avgTurnaround: s.paid > 0 ? (s.turnaroundSum / s.paid).toFixed(1) : '-'
      }))
      .sort((a, b) => b.value - a.value);

    const sortedDepartments = Object.entries(departments)
      .map(([name, stats]) => ({ name, ...stats }))
      .sort((a, b) => b.value - a.value);

    // Sort trend keys chronologically if possible, otherwise by string
    const trendKeys = Object.keys(trendMap).sort((a, b) => {
      try { return parseISO(a) - parseISO(b); } catch { return 0; }
    });
    const trendValues = trendKeys.map(k => trendMap[k]);

    return {
      totalRecovered,
      recoveryPercent,
      avgTurnaround,
      outstandingCollections: outstandingItems.length,
      outstandingItems: outstandingItems.slice(0, 5),
      turnaroundBuckets,
      supplierRankings,
      departments: sortedDepartments,
      trendKeys,
      trendValues,
    };
  }, [claims, timeFilter]);

  const trendChartOptions = {
    tooltip: { 
      trigger: 'axis', 
      formatter: (params) => {
        const p = params[0];
        return `${p.name}<br/><span style="color:#1a5d3a;font-weight:bold">Sold Revenue: R ${p.value.toLocaleString()}</span>`;
      }
    },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { 
      type: 'category', 
      data: reportData.trendKeys, 
      axisLine: { lineStyle: { color: '#e5e7eb' } }, 
      axisLabel: { color: '#6b7280', fontSize: 10 } 
    },
    yAxis: { 
      type: 'value', 
      splitLine: { lineStyle: { color: '#f3f4f6', type: 'dashed' } }, 
      axisLabel: { color: '#6b7280', formatter: 'R {value}' } 
    },
    series: [{
      name: 'Sold Revenue',
      data: reportData.trendValues,
      type: 'line',
      smooth: true,
      symbolSize: 8,
      areaStyle: { 
        color: { 
          type: 'linear', x: 0, y: 0, x2: 0, y2: 1, 
          colorStops: [{ offset: 0, color: 'rgba(26,93,58,0.3)' }, { offset: 1, color: 'rgba(26,93,58,0.0)' }] 
        } 
      },
      itemStyle: { color: '#1a5d3a', borderWidth: 2, borderColor: '#fff' },
      lineStyle: { width: 3 }
    }]
  };

  const pieChartOptions = {
    tooltip: { trigger: 'item', formatter: '{b}: R {c} ({d}%)' },
    legend: { bottom: '0%', textStyle: { color: '#6b7280', fontSize: 10 }, icon: 'circle' },
    series: [{
      name: 'Sold Value by Dept',
      type: 'pie',
      radius: ['45%', '70%'],
      avoidLabelOverlap: false,
      itemStyle: { borderRadius: 10, borderColor: '#fff', borderWidth: 2 },
      label: { show: false },
      emphasis: { label: { show: true, fontSize: 14, fontWeight: 'bold' } },
      data: reportData.departments.map(d => ({ value: d.value, name: d.name }))
    }]
  };

  const handleDownloadPDF = async () => {
    setIsGeneratingPDF(true);
    const element = document.getElementById('report-content');
    try {
      const canvas = await html2canvas(element, { scale: 2, useCORS: true, logging: false, backgroundColor: '#f4f7f6' });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`SalvageOS_Revenue_Report_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    } catch (err) {
      console.error("PDF generation failed:", err);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const MetricCard = ({ id, label, value, icon, subtext, color, active }) => (
    <button 
      onClick={() => setActiveMetric(id)}
      className={`text-left w-full transition-all duration-300 transform group ${active ? 'scale-[1.02]' : 'hover:scale-[1.01]'}`}
    >
      <Card className={`relative overflow-hidden border-2 transition-all ${active ? `border-${color}-500 shadow-lg ring-4 ring-${color}-500/10` : 'border-transparent shadow-sm'}`}>
        <div className="flex justify-between items-start mb-2">
          <p className={`text-xs font-bold uppercase tracking-wider ${active ? `text-${color}-600` : 'text-gray-400'}`}>
            {label}
          </p>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${active ? `bg-${color}-500 text-white` : `bg-${color}-50 text-${color}-600`}`}>
            <SafeIcon icon={icon} />
          </div>
        </div>
        <h3 className="text-2xl md:text-3xl font-black text-gray-900">
          {value}
        </h3>
        <p className={`text-xs font-medium mt-2 flex items-center gap-1 ${active ? `text-${color}-600` : 'text-gray-500'}`}>
          {subtext}
          {active && <SafeIcon icon={FiChevronRight} className="animate-pulse" />}
        </p>
        {active && <div className={`absolute bottom-0 left-0 h-1 bg-${color}-500 w-full`} />}
      </Card>
    </button>
  );

  if (loading) {
    return (
      <div className="py-20 flex flex-col items-center justify-center text-gray-400">
        <SafeIcon icon={FiLoader} className="animate-spin text-4xl mb-4 text-brand" />
        <p>Crunching financial data...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-12">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mb-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-1">
            {isSuperAdmin ? 'Revenue & Performance' : 'My Financial Statements'}
          </h1>
          <p className="text-gray-500">Tracking recovered value from sold salvage items.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="bg-white border border-gray-200 rounded-xl p-1 flex shadow-sm">
            {[{ id: 'month', label: 'Month' }, { id: 'quarter', label: 'Quarter' }, { id: 'year', label: 'Year' }, { id: 'all', label: 'All Time' }].map(t => (
              <button key={t.id} onClick={() => setTimeFilter(t.id)} className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${timeFilter === t.id ? 'bg-brand text-white shadow-sm' : 'text-gray-500 hover:bg-gray-100'}`} >
                {t.label}
              </button>
            ))}
          </div>
          <button onClick={handleDownloadPDF} disabled={isGeneratingPDF} className="flex items-center gap-2 px-5 py-2 bg-gray-900 text-white rounded-xl hover:bg-black font-medium text-sm transition-colors shadow-md disabled:opacity-50" >
            {isGeneratingPDF ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiDownload} />}
            {isGeneratingPDF ? 'Generating...' : 'Export Statement'}
          </button>
        </div>
      </div>

      <div id="report-content" className="space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <MetricCard 
            id="value" 
            label="Total Sold Revenue" 
            value={`R ${reportData.totalRecovered.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} 
            icon={FiDollarSign} 
            subtext="Actual recovered value" 
            color="brand" 
            active={activeMetric === 'value'} 
          />
          <MetricCard 
            id="recovery" 
            label="Recovery Yield" 
            value={`${reportData.recoveryPercent}%`} 
            icon={FiPercent} 
            subtext="Sold vs Estimated Value" 
            color="blue" 
            active={activeMetric === 'recovery'} 
          />
          <MetricCard 
            id="turnaround" 
            label="Avg Sale Speed" 
            value={`${reportData.avgTurnaround} Days`} 
            icon={FiClock} 
            subtext="Time to payment" 
            color="purple" 
            active={activeMetric === 'turnaround'} 
          />
          <MetricCard 
            id="outstanding" 
            label="Pending Pickup" 
            value={reportData.outstandingCollections} 
            icon={FiTruck} 
            subtext="Sold but not collected" 
            color="orange" 
            active={activeMetric === 'outstanding'} 
          />
        </div>

        {/* Dynamic Breakdown */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeMetric}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="w-full"
          >
            <Card className="bg-white border-2 border-gray-100 shadow-xl !p-0 overflow-hidden">
              <div className="bg-gray-50/80 px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                <h4 className="font-black text-gray-900 flex items-center gap-2 uppercase tracking-tighter">
                  <div className={`w-2 h-2 rounded-full bg-${activeMetric === 'value' ? 'brand' : activeMetric === 'recovery' ? 'blue' : activeMetric === 'turnaround' ? 'purple' : 'orange'}-500`} />
                  {activeMetric === 'value' ? 'Sold Items Revenue Streams' : 
                   activeMetric === 'recovery' ? 'Conversion Analysis' : 
                   activeMetric === 'turnaround' ? 'Processing Speed' : 'Collection Queue'}
                </h4>
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest bg-white px-2 py-1 rounded border border-gray-100">Live Financials</span>
              </div>
              <div className="p-6">
                {activeMetric === 'value' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {reportData.departments.slice(0, 3).map((dept, i) => (
                      <div key={i} className="bg-surface-alt p-4 rounded-2xl border border-gray-100">
                        <p className="text-[10px] font-black text-gray-400 uppercase mb-1">{dept.name}</p>
                        <p className="text-xl font-black text-brand">R {dept.value.toLocaleString()}</p>
                        <div className="w-full h-1.5 bg-gray-200 rounded-full mt-3 overflow-hidden">
                          <div className="h-full bg-brand" style={{ width: `${reportData.totalRecovered > 0 ? (dept.value / reportData.totalRecovered) * 100 : 0}%` }} />
                        </div>
                      </div>
                    ))}
                    {reportData.departments.length === 0 && <p className="text-gray-400 italic text-sm">No sales recorded in this period.</p>}
                  </div>
                )}

                {activeMetric === 'turnaround' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center p-6 bg-green-50 rounded-2xl border border-green-100">
                      <SafeIcon icon={FiZap} className="mx-auto mb-2 text-green-600" size={24} />
                      <h5 className="text-2xl font-black text-green-700">{reportData.turnaroundBuckets.fast}</h5>
                      <p className="text-[10px] font-bold text-green-500 uppercase">Express (&lt;2 Days)</p>
                    </div>
                    <div className="text-center p-6 bg-blue-50 rounded-2xl border border-blue-100">
                      <SafeIcon icon={FiTrendingUp} className="mx-auto mb-2 text-blue-600" size={24} />
                      <h5 className="text-2xl font-black text-blue-700">{reportData.turnaroundBuckets.standard}</h5>
                      <p className="text-[10px] font-bold text-blue-500 uppercase">Standard (3-4 Days)</p>
                    </div>
                    <div className="text-center p-6 bg-red-50 rounded-2xl border border-red-100">
                      <SafeIcon icon={FiAlertCircle} className="mx-auto mb-2 text-red-600" size={24} />
                      <h5 className="text-2xl font-black text-red-700">{reportData.turnaroundBuckets.slow}</h5>
                      <p className="text-[10px] font-bold text-red-500 uppercase">Delayed (&gt;5 Days)</p>
                    </div>
                  </div>
                )}

                {activeMetric === 'outstanding' && (
                  <div className="space-y-3">
                    {reportData.outstandingItems.length === 0 ? (
                      <p className="text-gray-400 italic text-center py-4 text-sm">All sold items have been successfully collected.</p>
                    ) : (
                      reportData.outstandingItems.map((item, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-surface-alt rounded-xl border border-gray-100">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center text-orange-500 shadow-sm border border-gray-100">
                              <SafeIcon icon={FiBox} />
                            </div>
                            <div>
                              <p className="text-sm font-bold text-gray-800">{item.claim_number}</p>
                              <p className="text-[10px] text-gray-500 uppercase font-bold">{item.salvage_item} • {item.salvagerName}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="text-[10px] font-black text-orange-600 bg-orange-100 px-2 py-1 rounded-md uppercase tracking-wider">Awaiting Pickup</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {activeMetric === 'recovery' && (
                  <div className="flex flex-col md:flex-row items-center gap-12">
                    <div className="flex-1 space-y-4 w-full">
                      <div className="flex justify-between items-center text-sm font-bold">
                        <span className="text-gray-500">Sold Conversion Rate</span>
                        <span className="text-green-600">{reportData.recoveryPercent}%</span>
                      </div>
                      <div className="w-full h-4 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
                        <div className="h-full bg-green-500" style={{ width: `${reportData.recoveryPercent}%` }} />
                      </div>
                      <p className="text-xs text-gray-500 leading-relaxed italic">
                        Yield is calculated by comparing the actual Sale Price (Offer Price) against the initial Estimated Value of the assets.
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4 w-full md:w-auto">
                       <div className="bg-blue-50 p-4 rounded-2xl text-center border border-blue-100">
                          <p className="text-2xl font-black text-blue-600">{reportData.supplierRankings.length}</p>
                          <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Active Buyers</p>
                       </div>
                       <div className="bg-green-50 p-4 rounded-2xl text-center border border-green-100">
                          <p className="text-2xl font-black text-green-600">Stable</p>
                          <p className="text-[10px] font-black text-green-400 uppercase tracking-widest">Yield Health</p>
                       </div>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </motion.div>
        </AnimatePresence>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          <Card className="lg:col-span-2 shadow-sm border border-gray-100">
            <h3 className="font-bold text-gray-900 mb-6 flex items-center gap-2">
              <SafeIcon icon={FiTrendingUp} className="text-brand" /> Sold Revenue Trend
            </h3>
            <div className="h-[300px] w-full">
              {reportData.trendKeys.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-gray-400 text-sm italic gap-2"> 
                  <SafeIcon icon={FiDollarSign} size={32} className="opacity-20" />
                  No finalized sales in this period. 
                </div>
              ) : (
                <ReactECharts option={trendChartOptions} style={{ height: '100%', width: '100%' }} />
              )}
            </div>
          </Card>
          <Card className="shadow-sm border border-gray-100">
            <h3 className="font-bold text-gray-900 mb-6 flex items-center gap-2">
              <SafeIcon icon={FiPieChart} className="text-brand" /> Sales by Department
            </h3>
            <div className="h-[300px] w-full">
              {reportData.departments.length === 0 ? (
                <div className="h-full flex items-center justify-center text-gray-400 text-sm italic"> No category data available. </div>
              ) : (
                <ReactECharts option={pieChartOptions} style={{ height: '100%', width: '100%' }} />
              )}
            </div>
          </Card>
        </div>

        {/* Supplier Ranking Table */}
        <Card className="shadow-sm !p-0 overflow-hidden border border-gray-100">
          <div className="p-6 border-b border-gray-100 bg-white">
            <h3 className="font-bold text-gray-900 flex items-center gap-2 uppercase tracking-tighter">
              <SafeIcon icon={FiAward} className="text-brand" /> {isSuperAdmin ? 'Buyer Performance Leaderboard' : 'My Recovery Performance'}
            </h3>
            <p className="text-xs text-gray-500 mt-1 font-medium">Ranked by total payout value generated during the selected period.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 text-[10px] uppercase text-gray-400 tracking-widest font-black">
                  <th className="px-6 py-4 w-16 text-center">Rank</th>
                  <th className="px-6 py-4">Buyer / Salvager</th>
                  <th className="px-6 py-4 text-center">Sales</th>
                  <th className="px-6 py-4 text-center">Yield %</th>
                  <th className="px-6 py-4 text-center">Avg Days</th>
                  <th className="px-6 py-4 text-right">Total Recovered</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-sm">
                {reportData.supplierRankings.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-gray-400 italic"> No buyer activity registered in this period. </td>
                  </tr>
                ) : (
                  reportData.supplierRankings.map((supplier, index) => (
                    <tr key={index} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4 text-center font-black text-gray-300">{index + 1}</td>
                      <td className="px-6 py-4 font-bold text-gray-900">{supplier.name}</td>
                      <td className="px-6 py-4 text-center font-medium text-gray-600">{supplier.paid}</td>
                      <td className="px-6 py-4 text-center">
                        <span className={`px-2 py-1 rounded-md text-[10px] font-bold ${supplier.recoveryRate >= 80 ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                          {supplier.recoveryRate}%
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center font-medium text-gray-600">{supplier.avgTurnaround}</td>
                      <td className="px-6 py-4 text-right font-black text-gray-900"> R {supplier.value.toLocaleString(undefined, { maximumFractionDigits: 0 })} </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
};